/**
 * RefrigerantReport — REFRIG-01 (Seth 2026-09-25: refrigerant "easily tracked
 * on the job and inventory"). Every pound in and out, from what the crews log:
 *
 *   · Stock on the shelf — per refrigerant, counted by weight. Nothing moves
 *     until the first count (so a guess never goes negative); after that each
 *     closed job takes off what was weighed in.
 *   · Recovery tanks — what's in each (recovered − sent out) and a Send Out
 *     for the reclaimer / supply-house buyback.
 *   · By month — pounds recovered, sent out and charged per refrigerant: the
 *     EPA Section 608 disposal record (recovered from old units each month,
 *     shipped for reclaim and to whom) kept without a paper log.
 *   · The log — every entry with the customer, the unit and the tank. CSV.
 * Competitor frame: ServiceTitan has no refrigerant module (a per-lb price-book
 * item + a custom form); RunaCall-style apps keep a per-job log and an EPA
 * export. Pegasus does both from the charge / recovery the crew already logs.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, ExternalLink, Plus, Scale, Send, Snowflake } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { useMemberName } from "@/components/TeamMemberSelect";
import {
  REFRIGERANTS, ozText, useRefrigerantItems, useCountRefrigerant, useRefrigerantTanks, useAddTank, useTankHandoff,
  type RefrigerantLog, type RefrigerantTank,
} from "@/features/jobs/refrigerant";
import { downloadCsv } from "./csv";

type Kind = RefrigerantLog["kind"];
const KIND_WORD: Record<Kind, string> = { charge: "Charged", recover: "Recovered", handoff: "Sent out", adjust: "Stock count" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const lb1 = (oz: number) => `${(Math.round((oz / 16) * 10) / 10).toLocaleString()} lb`;
const dateOf = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const partKey = (r: string | null | undefined) => (r ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

interface Row extends RefrigerantLog {
  customer: string | null; address: string | null; custno: string | null; locno: string | null; jobTitle: string | null; jobClosed: boolean;
  unit: string | null; tank: string | null;
}

function cleanName(fullname: string | null): string | null {
  if (!fullname) return null;
  let s = fullname.replace(/\s+\d{4,}\s*$/, "").trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) s = `${m[2]} ${m[1]}`.trim();
  return s || null;
}

function useRefrigerantLog(year: number) {
  return useQuery({
    queryKey: ["refrigerant_report", year],
    queryFn: async (): Promise<Row[]> => {
      const from = `${year}-01-01T00:00:00`, to = `${year + 1}-01-01T00:00:00`;
      const { data, error } = await supabase.from("pegasus_refrigerant_log").select("*")
        .gte("created_at", from).lt("created_at", to).order("created_at", { ascending: false }).limit(2000);
      if (error) throw error;
      const logs = (data ?? []) as RefrigerantLog[];
      const ids = (k: keyof RefrigerantLog) => [...new Set(logs.map((l) => l[k]).filter(Boolean) as string[])];
      const jobIds = ids("job_id"), unitIds = ids("job_equipment_id"), oldIds = ids("removed_equipment_id"), tankIds = ids("tank_id");
      const [jobs, units, olds, tanks] = await Promise.all([
        jobIds.length ? supabase.from("pegasus_job").select("id, title, custno, locno, status, site_address, site_city").in("id", jobIds) : Promise.resolve({ data: [] }),
        unitIds.length ? supabase.from("pegasus_job_equipment").select("id, category, manufacturer, model, serial").in("id", unitIds) : Promise.resolve({ data: [] }),
        oldIds.length ? supabase.from("pegasus_job_removed_equipment").select("id, category, brand, model, serial").in("id", oldIds) : Promise.resolve({ data: [] }),
        tankIds.length ? supabase.from("pegasus_refrigerant_tank").select("id, label").in("id", tankIds) : Promise.resolve({ data: [] }),
      ]);
      type J = { id: string; title: string | null; custno: string; locno: string | null; status: string; site_address: string | null; site_city: string | null };
      const jobMap = new Map(((jobs.data ?? []) as J[]).map((j) => [j.id, j]));
      const custnos = [...new Set([...jobMap.values()].map((j) => j.custno).filter(Boolean))];
      const [custs, locs] = custnos.length
        ? await Promise.all([
            supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
            supabase.from("mirror_location").select("custno, locno, add1, city").in("custno", custnos),
          ])
        : [{ data: [] }, { data: [] }];
      const cMap = new Map(((custs.data ?? []) as { custno: string; fullname: string | null }[]).map((c) => [c.custno, cleanName(c.fullname)]));
      const lMap = new Map(((locs.data ?? []) as { custno: string; locno: string | null; add1: string | null; city: string | null }[]).map((l) => [`${l.custno}|${l.locno}`, l]));
      const uMap = new Map(((units.data ?? []) as { id: string; category: string | null; manufacturer: string | null; model: string | null; serial: string | null }[])
        .map((u) => [u.id, [u.category, [u.manufacturer, u.model].filter(Boolean).join(" "), u.serial ? `S/N ${u.serial}` : null].filter(Boolean).join(" · ")]));
      const oMap = new Map(((olds.data ?? []) as { id: string; category: string | null; brand: string | null; model: string | null; serial: string | null }[])
        .map((o) => [o.id, [`Old ${(o.category || "unit").toLowerCase()}`, [o.brand, o.model].filter(Boolean).join(" "), o.serial ? `S/N ${o.serial}` : null].filter(Boolean).join(" · ")]));
      const tMap = new Map(((tanks.data ?? []) as { id: string; label: string }[]).map((t) => [t.id, t.label]));
      return logs.map((l) => {
        const j = l.job_id ? jobMap.get(l.job_id) : undefined;
        const loc = j ? lMap.get(`${j.custno}|${j.locno}`) ?? null : null;
        const addr = j?.site_address ? `${j.site_address}${j.site_city ? ", " + j.site_city : ""}` : loc ? `${loc.add1 ?? ""}${loc.city ? ", " + loc.city : ""}` : null;
        return {
          ...l, oz: Number(l.oz || 0),
          customer: j ? cMap.get(j.custno) ?? j.custno : null, address: addr, custno: j?.custno ?? null, locno: j?.locno ?? null,
          jobTitle: j?.title ?? null, jobClosed: j?.status === "closed",
          unit: l.job_equipment_id ? uMap.get(l.job_equipment_id) ?? null : l.removed_equipment_id ? oMap.get(l.removed_equipment_id) ?? null : null,
          tank: l.tank_id ? tMap.get(l.tank_id) ?? null : null,
        };
      });
    },
  });
}

/** When each refrigerant was last counted (the latest adjust row per item). */
function useLastCounts() {
  return useQuery({
    queryKey: ["refrigerant_report", "counts"],
    queryFn: async (): Promise<Map<string, string>> => {
      const { data } = await supabase.from("pegasus_refrigerant_log").select("item_id, created_at")
        .eq("kind", "adjust").not("item_id", "is", null).order("created_at", { ascending: false }).limit(200);
      const m = new Map<string, string>();
      for (const r of (data ?? []) as { item_id: string; created_at: string }[]) if (!m.has(r.item_id)) m.set(r.item_id, r.created_at);
      return m;
    },
  });
}

/** Pounds logged on jobs that are still open — they come off the shelf when the job closes. */
function useOpenJobCharges() {
  return useQuery({
    queryKey: ["refrigerant_report", "open_charges"],
    queryFn: async (): Promise<Map<string, number>> => {
      const { data } = await supabase.from("pegasus_refrigerant_log").select("item_id, oz, job:pegasus_job!inner(status)")
        .eq("kind", "charge").not("item_id", "is", null).neq("job.status", "closed");
      const m = new Map<string, number>();
      for (const r of (data ?? []) as unknown as { item_id: string; oz: number }[]) m.set(r.item_id, (m.get(r.item_id) ?? 0) + Number(r.oz || 0));
      return m;
    },
  });
}

export function RefrigerantReport() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [kind, setKind] = useState<Kind | "all">("all");
  const { data, isFetching } = useRefrigerantLog(year);
  const rows = useMemo(() => data ?? [], [data]);
  // Who logged it: the installer (by empno — View-As / shared iPads log under the office login), else the login.
  const memberName = useMemberName();
  const byName = (r: Row) => memberName(r.created_by_empno) ?? (r.created_by ? r.created_by.split("@")[0] : "");
  const shown = kind === "all" ? rows : rows.filter((r) => r.kind === kind);

  // By month × refrigerant: recovered, sent out, charged (the EPA monthly record).
  const byMonth = useMemo(() => {
    const types = [...new Set(rows.filter((r) => r.kind !== "adjust").map((r) => r.refrigerant))].sort();
    const cell = new Map<string, number>();
    for (const r of rows) {
      if (r.kind === "adjust") continue;
      const k = `${new Date(r.created_at).getMonth()}|${r.kind}|${r.refrigerant}`;
      cell.set(k, (cell.get(k) ?? 0) + r.oz);
    }
    const months = [...new Set(rows.filter((r) => r.kind !== "adjust").map((r) => new Date(r.created_at).getMonth()))].sort((a, b) => b - a);
    return { types, months, get: (m: number, k: Kind, t: string) => cell.get(`${m}|${k}|${t}`) ?? 0 };
  }, [rows]);
  const total = (k: Kind) => rows.filter((r) => r.kind === k).reduce((s, r) => s + r.oz, 0);

  const exportCsv = () => downloadCsv(`refrigerant-log-${year}`, [
    ["Date", "What", "Refrigerant", "Pounds", "Ounces", "Customer", "Address", "Job", "Unit", "Tank", "Sent to", "Empty (leaked out)", "Logged by", "Note"],
    ...shown.map((r) => [
      new Date(r.created_at).toLocaleDateString("en-CA"), KIND_WORD[r.kind], r.refrigerant, Math.round((r.oz / 16) * 100) / 100, r.oz,
      r.customer ?? "", r.address ?? "", r.jobTitle ?? "", r.unit ?? "", r.tank ?? "", r.handoff_to ?? "", r.empty ? "yes" : "",
      byName(r), r.note ?? "",
    ]),
  ]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year"
          className="rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm text-slate-700">
          {[thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="inline-flex overflow-hidden rounded-md border border-hairline">
          {(["all", "recover", "charge", "handoff", "adjust"] as const).map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              className={cn("px-3 py-1.5 text-sm", kind === k ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
              {k === "all" ? "All" : k === "adjust" ? "Counts" : KIND_WORD[k]}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{shown.length} entr{shown.length === 1 ? "y" : "ies"}</span>
        {shown.length > 0 && (
          <button type="button" onClick={exportCsv}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Download className="h-4 w-4" /> CSV
          </button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <StockCard />
        <TanksCard />
        <section className="rounded-xl border border-hairline bg-white p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Snowflake className="h-4 w-4 text-brand-500" /> {year} So Far
            <InfoTip label="Where these numbers come from">From Log the Charge and Log Refrigerant Recovered on the installers&apos; phones.</InfoTip>
          </h3>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Charged into new systems</dt><dd className="font-semibold tabular-nums text-slate-800">{ozText(total("charge"))}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Recovered from old units</dt><dd className="font-semibold tabular-nums text-slate-800">{ozText(total("recover"))}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Sent out for reclaim</dt><dd className="font-semibold tabular-nums text-slate-800">{ozText(total("handoff"))}</dd></div>
          </dl>
        </section>
      </div>

      {byMonth.months.length > 0 && (
        <section className="overflow-x-auto rounded-xl border border-hairline bg-white">
          <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">By Month</span>
            <InfoTip label="The EPA record">EPA Section 608: for old units we take out, keep where and when the refrigerant was recovered, what type and how much, and each month what was sent for reclaim and to whom. Keep it 3 years. This table plus the log below is that record.</InfoTip>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2">Month</th><th className="px-3 py-2">Recovered</th><th className="px-3 py-2">Sent out</th><th className="px-3 py-2">Charged</th>
              </tr>
            </thead>
            <tbody>
              {byMonth.months.map((m) => {
                const cellText = (k: Kind) => byMonth.types.map((t) => [t, byMonth.get(m, k, t)] as const).filter(([, oz]) => oz > 0).map(([t, oz]) => `${t} ${lb1(oz)}`).join(" · ") || "—";
                return (
                  <tr key={m} className="border-b border-hairline last:border-0">
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">{MONTHS[m]} {year}</td>
                    <td className="px-3 py-2 text-slate-600">{cellText("recover")}</td>
                    <td className="px-3 py-2 text-slate-600">{cellText("handoff")}</td>
                    <td className="px-3 py-2 text-slate-600">{cellText("charge")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">Date</th><th className="px-3 py-2">What</th><th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Where</th><th className="px-3 py-2">Unit / Tank</th><th className="px-3 py-2">By</th><th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {isFetching && !data && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!isFetching && shown.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Nothing logged{kind === "all" ? "" : ` as ${kind === "adjust" ? "a count" : KIND_WORD[kind].toLowerCase()}`} in {year} yet.</td></tr>}
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-hairline align-top last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{dateOf(r.created_at)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">{KIND_WORD[r.kind]}<div className="text-xs font-semibold text-slate-500">{r.refrigerant}</div></td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-800">
                  {r.kind === "recover" && r.empty ? <span className="text-slate-500">Empty — leaked out</span>
                    : r.kind === "adjust" ? <span>{r.oz >= 0 ? "+" : "−"}{ozText(Math.abs(r.oz))}</span> : ozText(r.oz)}
                  {r.kind === "charge" && r.total_oz != null && <div className="text-xs text-slate-400">{ozText(r.total_oz)} in the system</div>}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {r.customer ?? (r.kind === "handoff" ? r.handoff_to : r.kind === "adjust" ? (r.note ?? "") : "—")}
                  {r.address && <div className="text-xs text-slate-400">{r.address}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{[r.unit, r.tank ? `Tank: ${r.tank}` : null].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{byName(r)}</td>
                <td className="px-3 py-2">
                  {r.custno && r.job_id && <Link to={`/jobs/${r.job_id}`} title="Open the job" className="text-slate-300 hover:text-brand-600"><ExternalLink className="h-4 w-4" /></Link>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockCard() {
  const { data: items } = useRefrigerantItems();
  const { data: counted } = useLastCounts();
  const { data: open } = useOpenJobCharges();
  const count = useCountRefrigerant();
  const [editing, setEditing] = useState<string | null>(null);
  const [lb, setLb] = useState("");
  const list = [...(items ?? [])].sort((a, b) => {
    const ia = REFRIGERANTS.findIndex((r) => partKey(r) === partKey(a.part_number));
    const ib = REFRIGERANTS.findIndex((r) => partKey(r) === partKey(b.part_number));
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Scale className="h-4 w-4 text-brand-500" /> On the Shelf
        <InfoTip label="How stock works">Weigh what&apos;s on the shelf and enter it. From then on each closed job takes off the pounds its crew weighed in. Until the first count nothing moves, so a guess never goes negative.</InfoTip>
      </h3>
      <ul className="space-y-1.5 text-sm">
        {list.map((it) => {
          const name = (REFRIGERANTS.find((r) => partKey(r) === partKey(it.part_number)) ?? it.part_number ?? it.description ?? "Refrigerant") as string;
          const pending = open?.get(it.id) ?? 0;
          return (
            <li key={it.id} className="rounded-lg border border-hairline px-2.5 py-1.5">
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 font-semibold text-slate-800">{name}</span>
                <span className="min-w-0 flex-1 text-slate-600">
                  {it.qty_in_stock == null ? <span className="text-slate-400">Not counted yet</span> : <><b className="font-semibold tabular-nums text-slate-800">{Number(it.qty_in_stock).toLocaleString()} lb</b>{counted?.get(it.id) ? <span className="text-xs text-slate-400"> · counted {dateOf(counted.get(it.id)!)}</span> : null}</>}
                </span>
                <button type="button" onClick={() => { setEditing(editing === it.id ? null : it.id); setLb(it.qty_in_stock != null ? String(it.qty_in_stock) : ""); }}
                  className="shrink-0 text-xs font-medium text-brand-700 hover:text-brand-900">Count</button>
              </div>
              {pending > 0 && <div className="mt-0.5 text-xs text-slate-400">{ozText(pending)} logged on open jobs — comes off when they close</div>}
              {editing === it.id && (
                <form className="mt-1.5 flex items-center gap-2" onSubmit={(e) => {
                  e.preventDefault();
                  const v = Number(lb);
                  if (!Number.isFinite(v) || v < 0) return;
                  count.mutate({ itemId: it.id, refrigerant: name, lb: v, was: it.qty_in_stock == null ? null : Number(it.qty_in_stock) }, { onSuccess: () => setEditing(null) });
                }}>
                  <input autoFocus value={lb} onChange={(e) => setLb(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="lb on the shelf"
                    className="w-32 rounded-md border border-hairline px-2 py-1 text-sm" />
                  <button type="submit" disabled={lb === "" || count.isPending} className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white disabled:bg-brand-300">{count.isPending ? "Saving…" : "Save Count"}</button>
                </form>
              )}
            </li>
          );
        })}
        {list.length === 0 && <li className="text-slate-400">No refrigerant items in the catalog (category “Refrigerant”).</li>}
      </ul>
    </section>
  );
}

function TanksCard() {
  const { data: tanks } = useRefrigerantTanks();
  const add = useAddTank();
  const send = useTankHandoff();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [ref, setRef] = useState<string>("");
  const [sending, setSending] = useState<RefrigerantTank | null>(null);
  const [lb, setLb] = useState("");
  const [to, setTo] = useState("");
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Snowflake className="h-4 w-4 text-brand-500" /> Recovery Tanks
        <InfoTip label="Tanks">What the crews recover goes into a tank (they pick it on the phone). Send Out records a tank going to a reclaimer or a supply-house buyback — that&apos;s the &quot;shipped for reclaim, and to whom&quot; part of the EPA record.</InfoTip>
        <button type="button" onClick={() => setAdding((v) => !v)} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-900"><Plus className="h-3 w-3" /> New Tank</button>
      </h3>
      {adding && (
        <form className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2" onSubmit={(e) => {
          e.preventDefault();
          if (!label.trim()) return;
          add.mutate({ label, refrigerant: ref || null }, { onSuccess: () => { setAdding(false); setLabel(""); setRef(""); } });
        }}>
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Name — e.g. Tank 3 (gray)" className="min-w-[150px] flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-sm" />
          <select value={ref} onChange={(e) => setRef(e.target.value)} aria-label="Refrigerant" className="rounded-md border border-hairline bg-white px-2 py-1 text-sm">
            <option value="">Any / mixed</option>
            {REFRIGERANTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button type="submit" disabled={!label.trim() || add.isPending} className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white disabled:bg-brand-300">Add</button>
        </form>
      )}
      <ul className="space-y-1.5 text-sm">
        {(tanks ?? []).map((t) => (
          <li key={t.id} className="rounded-lg border border-hairline px-2.5 py-1.5">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-slate-800">{t.label}</span>
                <span className="text-xs text-slate-400"> · {t.refrigerant ?? "any"}</span>
                <span className="block text-slate-600"><b className="font-semibold tabular-nums">{ozText(t.contents_oz)}</b> in it{t.last_handoff_at ? <span className="text-xs text-slate-400"> · last sent out {dateOf(t.last_handoff_at)}</span> : null}</span>
              </span>
              <button type="button" disabled={t.contents_oz <= 0} onClick={() => { setSending(t); setLb(String(Math.round((t.contents_oz / 16) * 1000) / 1000)); setTo(""); }}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-900 disabled:text-slate-300"><Send className="h-3 w-3" /> Send Out</button>
            </div>
            {sending?.id === t.id && (
              <form className="mt-1.5 flex flex-wrap items-center gap-2" onSubmit={(e) => {
                e.preventDefault();
                const v = Number(lb);
                if (!Number.isFinite(v) || v <= 0 || !to.trim()) return;
                // The whole tank (the prefill) goes out to the ounce — no 0.4 oz left behind by rounding.
                const oz = Math.abs(v * 16 - t.contents_oz) < 0.1 ? t.contents_oz : Math.round(v * 16 * 10) / 10;
                send.mutate({ tank: t, oz, to }, { onSuccess: () => setSending(null) });
              }}>
                <input value={lb} onChange={(e) => setLb(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" aria-label="Pounds" className="w-20 rounded-md border border-hairline px-2 py-1 text-sm" />
                <span className="text-xs text-slate-500">lb to</span>
                <input autoFocus value={to} onChange={(e) => setTo(e.target.value)} placeholder="Who took it — e.g. Johnstone reclaim" className="min-w-[160px] flex-1 rounded-md border border-hairline px-2 py-1 text-sm" />
                <button type="submit" disabled={!to.trim() || !lb || send.isPending} className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white disabled:bg-brand-300">Save</button>
                <button type="button" onClick={() => setSending(null)} className="text-xs text-slate-500">Cancel</button>
              </form>
            )}
          </li>
        ))}
        {(tanks ?? []).length === 0 && !adding && <li className="text-slate-400">No tanks yet — add one here, or the crew adds one on the phone when they recover.</li>}
      </ul>
    </section>
  );
}
